import React from 'react'
import styled from 'styled-components'
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  Bar,
  Rectangle,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  type TooltipProps,
  type YAxisProps,
} from 'recharts'
import type {DonutTone} from './DonutCard'

/**
 * TrendChart
 *
 * DS wrapper around recharts for multi-series line / area trend charts
 * (spend trend, order pipeline, week-over-week branch trends, etc). Each
 * series renders one line; pass `area` to fill the area under it. Series
 * colors come from the `--viz-*` scale (same `DonutTone` set as DonutCard),
 * so charts stay consistent with the rest of the DS — no new tokens.
 *
 * The chart fills its container width; give the parent a width and pass
 * `height` (default 260). Numbers in axes + tooltip use the mono font.
 *
 * A `null` value is a gap: the line breaks there rather than joining its
 * neighbours or dropping to 0. A point with a gap (or the chart's edge) on both
 * sides has no line to draw, so it gets a dot of its own, and a sparse series
 * never reads as an empty chart. Only series that contain `null` get those dots.
 *
 * A series with `variant: 'reference'` is context rather than data: a dashed
 * neutral line (a team average, a target) that ignores `tone` and `area`.
 * `referenceLines` draws the same dashed line flat, at one value across the
 * whole chart.
 *
 * A series with `variant: 'bar'` draws a bar per x-tick, rounded at the top. A
 * `null` value draws no bar; a real 0 draws a thin sliver on the baseline, so
 * the two never read alike. `highlight` picks out bars (the current week) in
 * `highlightTone`, and marks each highlighted slot with `data-highlight="true"`.
 * `gapLabel` writes a mark such as '—' at the foot of each empty slot.
 *
 * `tooltipNote` adds a line of text to the hover tooltip for any x-tick. It is
 * the only way a tick where every series is `null` gets a tooltip, so it is
 * where a gap's reason goes ("No hosted orders this week").
 *
 * @example pipeline trend (3 series)
 *   <TrendChart
 *     data={weeks}                       // [{ week: 'W31', received: 42, pledges: 30, confirmed: 24 }, …]
 *     xKey="week"
 *     series={[
 *       { key: 'received', label: 'Orders received', tone: 'cyan', area: true },
 *       { key: 'pledges',  label: 'Pledges sent',    tone: 'blue' },
 *       { key: 'confirmed',label: 'Confirmed',       tone: 'navy-soft' },
 *     ]}
 *     tooltipFormatter={(v) => `${v} orders`}
 *   />
 *
 * @example fill ratio with a team-average reference line (gaps where there is no figure)
 *   <TrendChart
 *     data={weeks}                       // [{ week: 'Jul 6', staffer: 82, team: null, teamCounted: 1 }, …]
 *     xKey="week"
 *     series={[
 *       { key: 'staffer', label: 'Angelica', tone: 'cyan' },
 *       { key: 'team',    label: 'Team average', variant: 'reference' },
 *     ]}
 *     yTickFormatter={(v) => `${v}%`}
 *     tooltipFormatter={(v, s, row) =>
 *       s.key === 'team' ? `${v}% (${row.teamCounted} teammates)` : `${v}%`
 *     }
 *   />
 *
 * @example weekly bars, the current week highlighted, a flat team average, no axis or grid
 *   <TrendChart
 *     data={weeks}  // [{ week: 'W28', fill: 82 }, …, { week: 'W39', fill: null }]
 *     xKey="week"
 *     series={[{ key: 'fill', label: 'Fill rate', tone: 'navy', variant: 'bar' }]}
 *     highlight={(_row, i) => i === weeks.length - 1}
 *     tooltipNote={(row, i) =>
 *       row.fill === null ? 'No hosted orders this week' : i === weeks.length - 1 ? 'So far' : undefined
 *     }
 *     referenceLines={[{ value: 86.1, label: 'Team avg', id: 'team' }]}
 *     yAxis={false}
 *     grid={false}
 *     xTickInterval={0}                  // every week label, however narrow the card
 *     height={132}
 *   />
 */

/**
 * The reference series' stroke. `--neutral-600` is theme-swapped and holds at least 3:1 against the
 * card in both themes, enough for a meaningful graphic (WCAG 1.4.11).
 *
 * DS gap: there is no `--viz-reference` token for a neutral chart line. Raised rather than invented
 * (CLAUDE.md §1); swap this for the token once it exists.
 */
const REFERENCE_COLOR = 'var(--neutral-600)'

/**
 * `neutral` is `--neutral-600` here, not DonutCard's `--neutral-500`: a line or bar is the whole
 * mark, and `--neutral-500` is under 3:1 against the card (WCAG 1.4.11).
 */
const TONE_VARS: Record<DonutTone, string> = {
  cyan: 'var(--viz-cyan)',
  'cyan-soft': 'var(--viz-cyan-soft)',
  blue: 'var(--viz-blue)',
  'blue-soft': 'var(--viz-blue-soft)',
  navy: 'var(--viz-navy)',
  'navy-soft': 'var(--viz-navy-soft)',
  warning: 'var(--warning-500)',
  error: 'var(--error-500)',
  neutral: REFERENCE_COLOR,
}

/** The reference series' dash, in SVG user units like recharts' other numeric props. */
const REFERENCE_DASH = '6 4'
/** An isolated point's dot: the hovered point's size (`activeDot`), so the two read as one mark. */
const ISOLATED_DOT_RADIUS = 4
/** A bar's corners, top-left → bottom-left, in SVG user units: rounded at the top only. */
const BAR_RADIUS: [number, number, number, number] = [3, 3, 0, 0]
/** The least height a bar with a value draws: a real 0 shows as a sliver, never as a gap. */
const MIN_BAR_HEIGHT = 2
/** How far a gap slot's label sits above the x-axis line, in SVG user units. */
const GAP_LABEL_LIFT = 4

/** Default color cycle when a series omits `tone`. */
const DEFAULT_TONES: DonutTone[] = ['cyan', 'blue', 'navy', 'cyan-soft', 'navy-soft', 'blue-soft']
const defaultTone = (i: number): DonutTone => DEFAULT_TONES[i % DEFAULT_TONES.length] ?? 'cyan'

export interface TrendSeries {
  /** Key into each data row. */
  key: string
  /** Legend + tooltip label. */
  label: string
  /** Color from the `--viz-*` scale; defaults cycle cyan → blue → navy. */
  tone?: DonutTone
  /** Render a low-opacity filled area under the line. */
  area?: boolean
  /** Plot against the left (default) or a secondary right axis — e.g. a different unit. */
  axis?: 'left' | 'right'
  /**
   * `'reference'`: a dashed neutral line for context, such as a team average, drawn without a
   * hover dot. It ignores `tone` and `area`.
   * `'bar'`: a bar per x-tick in `tone`, rounded at the top; a `null` value draws no bar. It
   * ignores `area`. Default `'line'`.
   */
  variant?: 'line' | 'reference' | 'bar'
}

/** A flat line across the chart at one value, such as a team average over the whole period. */
export interface TrendReferenceLine {
  /** Where the line sits on the left y-axis. The axis extends to include it. */
  value: number
  /** The line's name: its legend entry and its SVG `<title>`. */
  label: string
  /** Suffixes the line's test id (`trend-reference-line-<id>`) and keys it. */
  id?: string
}

/** One x-tick's values. A `null` series value is a gap. */
export type TrendRow = Record<string, number | string | null>

export interface TrendChartProps {
  /** One object per x-tick; each carries the value for every series key, or `null` for a gap. */
  data: TrendRow[]
  /** One line (or area) per series. */
  series: TrendSeries[]
  /** Key in each row used for the x-axis (e.g. `'week'`, `'month'`). */
  xKey: string
  /** Chart height in px (default 260). */
  height?: number
  /** Format left y-axis ticks (e.g. `(v) => '$' + v / 1000 + 'k'`). */
  yTickFormatter?: (value: number) => string
  /**
   * Left y-axis domain. Defaults to recharts' `[0, 'auto']` (a 0 baseline). Pass a
   * negative-aware domain — e.g. `[(min) => Math.min(0, min), 'auto']` — for series
   * that can dip below zero (e.g. margin) so the axis extends downward instead of
   * clamping the floor to 0 and mis-scaling the plot.
   */
  yDomain?: YAxisProps['domain']
  /** Format the secondary right y-axis ticks (used when any series sets `axis: 'right'`). */
  rightTickFormatter?: (value: number) => string
  /**
   * Format the tooltip value for a series (default: raw value). `row` is the hovered x-tick's whole
   * data row, so a value can be described with another key (e.g. how many people an average counts).
   * A series with no value at the hovered tick is left out of the tooltip.
   */
  tooltipFormatter?: (value: number, series: TrendSeries, row: TrendRow) => string
  /**
   * A line of text at the foot of the hover tooltip for a row, such as why a week has no figure or
   * that it is still in progress. Return `undefined` for none. It runs on every x-tick, gaps
   * included: a tick where every series is `null` shows its title and the note, where without a note
   * it shows no tooltip at all.
   */
  tooltipNote?: (row: TrendRow, index: number) => string | undefined
  /** Show the legend below the chart (default true). */
  legend?: boolean
  /**
   * Picks out bars: every bar series draws the rows it returns true for in `highlightTone`. Each
   * highlighted slot carries `data-highlight="true"`, on its bar or, for a `null` value, on the
   * empty slot. Line and area series ignore it.
   */
  highlight?: (row: TrendRow, index: number) => boolean
  /** The highlighted bars' color. Default `'cyan'`, `--viz-cyan`: an identity mark, never text. */
  highlightTone?: DonutTone
  /**
   * A mark, such as `'—'`, written at the foot of every empty bar slot (a `null` value). Bar series
   * only; by default an empty slot is blank. It is visual only (hidden from screen readers), so give
   * the reason in `tooltipNote` and in the chart's text alternative.
   */
  gapLabel?: string
  /** Flat dashed neutral lines, in the reference series' style, drawn behind the series. */
  referenceLines?: TrendReferenceLine[]
  /** Show the y-axes' ticks and labels (default true). The scale still applies when hidden. */
  yAxis?: boolean
  /** Show the horizontal grid lines (default true). */
  grid?: boolean
  /**
   * Draw every (n + 1)th x-tick label; 0 draws them all. Default: recharts' `'preserveEnd'`, which
   * drops labels that would collide.
   */
  xTickInterval?: number
  'data-testid'?: string
}

const Wrap = styled.div`
  width: 100%;
  font-family: var(--font-sans);
`

const TooltipBox = styled.div`
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-elevated);
  padding: var(--space-2) var(--space-3);
  font-size: var(--fs-caption);
`
const TooltipTitle = styled.div`
  font-weight: var(--fw-semibold);
  color: var(--text-primary);
  margin-bottom: var(--space-1);
`
const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--text-secondary);
  line-height: 1.6;
`
const Dot = styled.span<{$color: string}>`
  width: 8px;
  height: 8px;
  border-radius: var(--radius-pill);
  background: ${({$color}) => $color};
  flex-shrink: 0;
`
/** The reference series' swatch, in the legend and the tooltip: a short dashed rule. */
const LegendDash = styled.span`
  width: var(--space-4);
  border-top-width: 2px;
  border-top-style: dashed;
  border-top-color: ${REFERENCE_COLOR};
  flex-shrink: 0;
`
const TooltipValue = styled.span`
  font-family: var(--font-mono);
  font-weight: var(--fw-semibold);
  color: var(--text-primary);
  margin-left: var(--space-4);
`
/** `tooltipNote`'s line. It wraps at the DS Tooltip bubble's width rather than widening the box. */
const TooltipNote = styled.div`
  color: var(--text-secondary);
  line-height: var(--lh-normal);
  max-width: 320px;

  ${TooltipRow} + & {
    margin-top: var(--space-1);
  }
`
/** `gapLabel`'s mark in an empty bar slot: caption-sized secondary text, like the axis ticks. */
const GapLabel = styled.text`
  font-family: var(--font-sans);
  font-size: var(--fs-caption);
  fill: var(--text-secondary);
`
const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-4);
  margin-top: var(--space-3);
  font-size: var(--fs-caption);
  color: var(--text-secondary);
`
const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
`

const AXIS_TICK = {fontSize: 11, fontFamily: 'var(--font-mono)', fill: 'var(--text-secondary)'}

const isReference = (s: TrendSeries): boolean => s.variant === 'reference'
const isBar = (s: TrendSeries): boolean => s.variant === 'bar'

/** What recharts hands a `dot` render function: the point, and its index into `data`. */
interface DotRenderProps {
  key?: React.Key
  cx?: number
  cy?: number
  index?: number
}

/**
 * What recharts hands a bar `shape` function: the bar's box, its row, its index into `data`, and
 * `background`, the slot's full-height box over the plot area.
 */
interface BarShapeProps {
  x?: number
  y?: number
  width?: number
  height?: number
  index?: number
  payload?: TrendRow
  background?: {y?: number; height?: number}
}

/**
 * Whether a series' point stands alone: it has a value, and the rows either side of it have none or
 * do not exist. A line needs two neighbouring values, so without a dot such a point draws nothing.
 */
function isIsolatedPoint(data: TrendRow[], key: string, index: number): boolean {
  const valueAt = (i: number) => data[i]?.[key]
  return valueAt(index) != null && valueAt(index - 1) == null && valueAt(index + 1) == null
}

export function TrendChart({
  data,
  series,
  xKey,
  height = 260,
  yTickFormatter,
  yDomain,
  rightTickFormatter,
  tooltipFormatter,
  tooltipNote,
  legend = true,
  highlight,
  highlightTone = 'cyan',
  gapLabel,
  referenceLines = [],
  yAxis = true,
  grid = true,
  xTickInterval,
  'data-testid': dataTestId,
}: TrendChartProps) {
  const colorFor = (s: TrendSeries, i: number): string =>
    isReference(s) ? REFERENCE_COLOR : TONE_VARS[s.tone ?? defaultTone(i)]
  const highlightColor = TONE_VARS[highlightTone]
  const isHighlighted = (row: TrendRow | undefined, index: number | undefined): boolean =>
    !!highlight && row !== undefined && index !== undefined && index >= 0 && highlight(row, index)

  /**
   * A series' `dot`. Series with no gaps keep recharts' `false`, exactly as before gaps existed. A
   * series with a gap draws a dot only at its isolated points (isIsolatedPoint), and an empty group
   * everywhere else.
   */
  const dotFor = (s: TrendSeries, color: string) => {
    if (!data.some((row) => row[s.key] === null)) return false
    const renderIsolatedDot = ({key, cx, cy, index}: DotRenderProps) =>
      typeof cx === 'number' &&
      typeof cy === 'number' &&
      index !== undefined &&
      isIsolatedPoint(data, s.key, index) ? (
        <circle
          key={key}
          cx={cx}
          cy={cy}
          r={ISOLATED_DOT_RADIUS}
          fill={color}
          stroke="none"
          className="trend-chart-isolated-dot"
        />
      ) : (
        <g key={key} />
      )
    return renderIsolatedDot
  }

  /** `gapLabel` in an empty slot, centred on it just above the foot of the plot (the x-axis). */
  const renderGapLabel = ({x, width, background}: BarShapeProps) => {
    const top = background?.y
    const plotHeight = background?.height
    if (!gapLabel || x == null || width == null || top == null || plotHeight == null) return null
    return (
      <GapLabel
        x={x + width / 2}
        y={top + plotHeight - GAP_LABEL_LIFT}
        textAnchor="middle"
        aria-hidden="true"
        className="trend-chart-gap-label">
        {gapLabel}
      </GapLabel>
    )
  }

  /**
   * A bar series' `shape`: one slot per row. A `null` value is an empty group, never a bar of 0.
   * A highlighted slot is drawn in `highlightTone` and carries `data-highlight`, which recharts'
   * `Cell` does not pass through to the bar, hence the custom shape.
   */
  const barShapeFor = (s: TrendSeries, color: string) => {
    const renderBar = (props: unknown) => {
      const bar = props as BarShapeProps
      const lit = isHighlighted(bar.payload, bar.index)
      // React leaves an undefined attribute off, so only highlighted slots carry it.
      const mark = lit ? 'true' : undefined
      if (bar.payload?.[s.key] == null)
        return (
          <g className="trend-chart-bar-gap" data-highlight={mark}>
            {renderGapLabel(bar)}
          </g>
        )
      return (
        <Rectangle
          x={bar.x}
          y={bar.y}
          width={bar.width}
          height={bar.height}
          radius={BAR_RADIUS}
          fill={lit ? highlightColor : color}
          className="trend-chart-bar"
          data-highlight={mark}
        />
      )
    }
    return renderBar
  }

  /** A series' swatch in the legend and the tooltip: a dot, or a dashed rule for a reference. */
  const swatchFor = (s: TrendSeries | undefined, color: string) =>
    s && isReference(s) ? (
      <LegendDash data-testid="trend-reference-swatch" />
    ) : (
      <Dot $color={color} />
    )

  const hasRightAxis = series.some((s) => s.axis === 'right')
  // Without a y-axis the plot takes the right margin on the left too, so it sits centred and the
  // first x-tick label is not clipped.
  const margin = {top: 8, right: 12, bottom: 4, left: yAxis ? 0 : 12}

  const renderTooltip = (props: TooltipProps<number, string>) => {
    if (!props.active) return null
    // Every entry carries the hovered row, a gap's entry included.
    const hoveredRow = props.payload?.[0]?.payload as TrendRow | undefined
    const hoveredIndex = hoveredRow ? data.indexOf(hoveredRow) : -1
    const note =
      hoveredRow && hoveredIndex >= 0 ? tooltipNote?.(hoveredRow, hoveredIndex) : undefined
    const entries = props.payload ?? []
    if (!entries.some((entry) => entry.value != null) && !note) return null
    return (
      <TooltipBox>
        <TooltipTitle>{props.label}</TooltipTitle>
        {entries.map((entry) => {
          // A gap has no value to show. Without a note recharts' filterNull drops these already; with
          // one it keeps them (see <Tooltip>).
          if (entry.value === null || entry.value === undefined) return null
          const s = series.find((x) => x.key === entry.dataKey)
          const value = entry.value
          const row = (entry.payload ?? {}) as TrendRow
          // A highlighted bar's swatch takes the highlight color, to match the bar it describes.
          const litBar = !!s && isBar(s) && isHighlighted(row, data.indexOf(row))
          return (
            <TooltipRow key={String(entry.dataKey)}>
              {swatchFor(s, litBar ? highlightColor : (entry.color as string) ?? 'var(--viz-cyan)')}
              {s?.label ?? entry.name}
              <TooltipValue>
                {tooltipFormatter && s ? tooltipFormatter(value, s, row) : value}
              </TooltipValue>
            </TooltipRow>
          )
        })}
        {note && <TooltipNote data-testid="trend-tooltip-note">{note}</TooltipNote>}
      </TooltipBox>
    )
  }

  return (
    <Wrap data-testid={dataTestId}>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={margin}>
          {grid && (
            <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="3 3" vertical={false} />
          )}
          <XAxis
            dataKey={xKey}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{stroke: 'var(--border-subtle)'}}
            interval={xTickInterval}
          />
          <YAxis
            yAxisId="left"
            hide={!yAxis}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            tickFormatter={yTickFormatter}
            domain={yDomain}
            width={48}
          />
          {hasRightAxis && (
            <YAxis
              yAxisId="right"
              orientation="right"
              hide={!yAxis}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              tickFormatter={rightTickFormatter}
              width={48}
            />
          )}
          {/* recharts drops null entries and hides the box when none are left. A note must still
              show on a tick where every series is a gap, so with one the entries are kept. */}
          <Tooltip
            content={renderTooltip}
            cursor={{stroke: 'var(--neutral-300)'}}
            filterNull={!tooltipNote}
          />
          {/* Before the series, so the bars and lines draw over the reference lines. */}
          {referenceLines.map((line, i) => (
            <ReferenceLine
              key={line.id ?? `reference-${i}`}
              yAxisId="left"
              y={line.value}
              ifOverflow="extendDomain"
              stroke={REFERENCE_COLOR}
              strokeDasharray={REFERENCE_DASH}
              strokeWidth={2}
              data-testid={line.id ? `trend-reference-line-${line.id}` : 'trend-reference-line'}
              shape={(props: React.SVGProps<SVGLineElement>) => (
                <line {...props} className="recharts-reference-line-line">
                  <title>{line.label}</title>
                </line>
              )}
            />
          ))}
          {series.map((s, i) =>
            isBar(s) ? (
              <Bar
                key={s.key}
                yAxisId={s.axis ?? 'left'}
                dataKey={s.key}
                name={s.label}
                fill={colorFor(s, i)}
                minPointSize={MIN_BAR_HEIGHT}
                shape={barShapeFor(s, colorFor(s, i))}
              />
            ) : isReference(s) ? (
              <Line
                key={s.key}
                yAxisId={s.axis ?? 'left'}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={REFERENCE_COLOR}
                strokeDasharray={REFERENCE_DASH}
                strokeWidth={2}
                connectNulls={false}
                dot={dotFor(s, REFERENCE_COLOR)}
                activeDot={false}
              />
            ) : s.area ? (
              <Area
                key={s.key}
                yAxisId={s.axis ?? 'left'}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={colorFor(s, i)}
                fill={colorFor(s, i)}
                fillOpacity={0.08}
                strokeWidth={2.5}
                baseValue={0}
                connectNulls={false}
                dot={dotFor(s, colorFor(s, i))}
                activeDot={{r: ISOLATED_DOT_RADIUS}}
              />
            ) : (
              <Line
                key={s.key}
                yAxisId={s.axis ?? 'left'}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={colorFor(s, i)}
                strokeWidth={2.5}
                connectNulls={false}
                dot={dotFor(s, colorFor(s, i))}
                activeDot={{r: ISOLATED_DOT_RADIUS}}
              />
            ),
          )}
        </ComposedChart>
      </ResponsiveContainer>
      {legend && (
        <Legend>
          {series.map((s, i) => (
            <LegendItem key={s.key}>
              {swatchFor(s, colorFor(s, i))}
              {s.label}
            </LegendItem>
          ))}
          {referenceLines.map((line, i) => (
            <LegendItem key={line.id ?? `reference-${i}`}>
              <LegendDash data-testid="trend-reference-swatch" />
              {line.label}
            </LegendItem>
          ))}
        </Legend>
      )}
    </Wrap>
  )
}
